import { Button, buttonVariants } from "@/components/ui/button.tsx";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card.tsx";
import { SystemStatusBanner } from "@/components/navigation/system-status-banner.tsx";
import { m } from "@/paraglide/messages.js";
import {
	type BootFailureKind,
	useStatusPageUrl,
} from "@/services/status-service.ts";
import { ExternalLink, RefreshCcw, ServerCrash, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Shown when restoring the session on app load fails for a non-auth reason
 * (backend unreachable, 5xx, network blip, or the bootstrap deadline expiring).
 * This is explicitly NOT a sign-out: the token is kept and the user can retry.
 * A genuine 401 never reaches this screen — the auth middleware redirects those
 * to login instead.
 *
 * Renders OUTSIDE the router (AuthProvider gates the router on the very promise
 * that just failed), which is why it mounts `SystemStatusBanner` itself. The
 * banner needs no router — only a QueryClientProvider, which sits above
 * AuthProvider — and it self-hides when the gateway reports everything healthy,
 * so it appears exactly when the platform really is the problem.
 */
export function AuthConnectionError({
	kind,
	retryAt,
	onRetry,
}: {
	kind: BootFailureKind;
	/**
	 * `Date.now()` milliseconds at which AuthProvider's own timer will retry, or
	 * null when nothing is scheduled. An absolute instant rather than a duration
	 * so this component can render a countdown without owning the schedule — the
	 * retry itself is the provider's job, since that is where the attempt count
	 * and the backoff live.
	 */
	retryAt: number | null;
	/** Retry now. Resets the backoff, because the user asked for "now". */
	onRetry: () => void;
}) {
	const secondsLeft = useCountdown(retryAt);
	const statusPageUrl = useStatusPageUrl();

	const isConnection = kind === "connection";

	return (
		<div className="min-h-screen flex flex-col">
			<SystemStatusBanner />
			<div className="flex-1 flex items-center justify-center p-4">
				<Card className="w-full max-w-md shadow-lg">
					<CardHeader className="text-center space-y-3 pb-6">
						<div className="flex justify-center">
							<div className="rounded-full bg-destructive/10 p-4">
								{isConnection ? (
									<WifiOff className="h-10 w-10 text-destructive" />
								) : (
									<ServerCrash className="h-10 w-10 text-destructive" />
								)}
							</div>
						</div>
						<CardTitle className="text-2xl font-bold">{title(kind)}</CardTitle>
						<CardDescription className="text-base">
							{description(kind)}
						</CardDescription>
					</CardHeader>

					{secondsLeft !== null && (
						<CardContent className="pb-0">
							<p className="text-center text-sm text-muted-foreground tabular-nums">
								{m.auth_connection_error_retry_countdown({
									seconds: secondsLeft,
								})}
							</p>
						</CardContent>
					)}

					<CardFooter className="flex flex-col gap-3 pt-6">
						<Button
							onClick={onRetry}
							variant="default"
							className="w-full gap-2"
						>
							<RefreshCcw className="h-4 w-4" />
							{m.error_actions_try_again()}
						</Button>
						{/*
						 * Always offered, even in the `connection` case. The banner above
						 * carries a status link only when it renders at all, and a user who
						 * disbelieves our "it's your network" verdict deserves a way to
						 * check for themselves.
						 */}
						<a
							href={statusPageUrl}
							target="_blank"
							rel="noreferrer"
							className={buttonVariants({
								variant: "outline",
								className: "w-full gap-2",
							})}
						>
							{m.system_status_view_full()}
							<ExternalLink className="h-3.5 w-3.5" />
						</a>
					</CardFooter>
				</Card>
			</div>
		</div>
	);
}

/**
 * Headline and body per classification.
 *
 * The outage case gets its OWN copy rather than reusing the status strip's
 * `system_status_summary_unavailable`. Those are the same words in a different
 * role: the strip's line is a full sentence sized for a one-line bar, and the
 * strip may be rendered directly above this card — so borrowing it printed the
 * identical sentence twice and left the body still talking about a connection
 * problem, contradicting its own headline.
 */
function title(kind: BootFailureKind): string {
	return kind === "platform_outage"
		? m.auth_connection_error_outage_title()
		: kind === "connection"
			? m.auth_connection_error_connection_title()
			: m.auth_connection_error_title();
}

function description(kind: BootFailureKind): string {
	return kind === "platform_outage"
		? m.auth_connection_error_outage_desc()
		: kind === "connection"
			? m.auth_connection_error_connection_desc()
			: m.auth_connection_error_description();
}

/**
 * Whole seconds until `retryAt`, or null when nothing is scheduled.
 *
 * Recomputed from the target instant on every tick rather than decremented from
 * a stored number, so a backgrounded tab whose timers were throttled resumes
 * showing the truth instead of a count that stopped where it was suspended.
 */
function useCountdown(retryAt: number | null): number | null {
	const [, setTick] = useState(0);

	useEffect(() => {
		if (retryAt === null) {
			return;
		}
		const interval = setInterval(() => setTick((t) => t + 1), 500);
		return () => clearInterval(interval);
	}, [retryAt]);

	if (retryAt === null) {
		return null;
	}
	return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
}
